<template lang="pug">
.members
  .seats-increase-warning-box.dialog-warning.blue.align-items-center(v-if="organization.show_per_seat_ui")
    i.d-flex.align-items-center.hi.hi-info-filled.hi-12.text-blue.pt-0

    div
      | Moving Project viewers to Users or Managers will move them from a free seat to a paid seat, and may increase the number of paid seats on your plan.&nbsp;
      a.text-nowrap(href="https://support.hubstaff.com/per-seat-pricing/")
        | Learn more

  #manager.form-group.project-members-zone-select
    .w-tw.w-full
      ZoneSelect(
        v-model="manager"
        multiple
        clearable
        searchable
        :fetcher="membersFetcher"
        server-all-ids
        :page-size="50"
        :resource="{ singular: 'member', plural: 'members' }"
        label="Managers"
        placeholder="Select managers"
        helper-text="Oversees and manages the project"
        :tooltip="disabled ? disabledTooltip : null"
        :disabled="disabled"
      )

  #user.form-group.project-members-zone-select
    .w-tw.w-full
      ZoneSelect(
        v-model="user"
        multiple
        clearable
        searchable
        :fetcher="membersFetcher"
        server-all-ids
        :page-size="50"
        :resource="{ singular: 'member', plural: 'members' }"
        label="Users"
        placeholder="Select users"
        helper-text="Works on the project, will not see other users (most common)"
        tooltip="Users who have member limits set within this project will not be removed."
      )

  #viewer.form-group.project-members-zone-select
    .w-tw.w-full
      ZoneSelect(
        v-model="viewer"
        multiple
        clearable
        searchable
        :fetcher="membersFetcher"
        server-all-ids
        :page-size="50"
        :resource="{ singular: 'member', plural: 'members' }"
        label="Viewers"
        placeholder="Select viewers"
        helper-text="Can view team reports for this project"
        :tooltip="disabled ? disabledTooltip : null"
        :disabled="disabled"
      )
</template>

<script>
import ZoneSelect from 'zone/components/base/Select.vue';
import { ProjectsResource } from '~/resources';
import { mapState, mapActions } from 'vuex';
import { createPaginatedSelectZoneFetcher } from '~/utils/paginated_select_zone_fetcher';

const MEMBER_PAGE_SIZE = 50;

export default {
  name: 'ProjectDialogTabMembers',
  components: { ZoneSelect },
  computed: {
    ...mapState({
      project: (state) => state.project,
      allMembers: (state) => ({ ...state.membersMap }),
      organization: (state) => state.organization,
      disabled: (state) => !state.organization.features.manager_roles.available,
      disabledTooltip: (state) => state.organization.restricted_tooltip,
      teamMemberIds: (state) => state.project.team_member_ids || [],
    }),
    projectId() {
      return this.project?.id || null;
    },
    organizationId() {
      return this.organization.id;
    },
    membersFetcher() {
      const organizationId = this.organizationId;
      const projectId = this.projectId;

      return createPaginatedSelectZoneFetcher({
        pageSize: MEMBER_PAGE_SIZE,
        selectedDisabledTooltip: 'Role set by a team. Edit it on the Teams tab.',
        mapOption: (item) => ({
          avatar: item.avatar,
          ...(item.removed ? { description: 'Removed' } : {}),
        }),
        request: (payload) =>
          ProjectsResource.members(
            { organization_id: organizationId },
            {
              ...payload,
              resource_id: projectId,
              search_name_only: true,
              ...(this.project.id ? { all_visible: false } : { restricted_lead_members: true }),
            },
            { abortReload: true },
          ),
      });
    },

    ...Object.fromEntries(
      ['manager', 'user', 'viewer'].map((role) => [
        role,
        {
          get() {
            return [...this.allMembers[role].values()];
          },
          set(items) {
            this.updateMembers({ items: this.membersForRole(items, role), role });
          },
        },
      ]),
    ),
  },
  methods: {
    ...mapActions(['updateMembers']),
    /** Team-sourced members stay on this role; picker rows are disabled via `selected_disabled_ids`. */
    membersForRole(items, role) {
      // New Project has no team-assigned members. Leftover `team_member_ids` from a previous
      // Edit dialog must not be re-applied when the picker is cleared.
      const locked = this.project?.id
        ? (this.teamMemberIds || []).filter((id) => this.allMembers[role].has(id))
        : [];

      return [...new Set([...items, ...locked])];
    },
  },
};
</script>
